import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import { IridologyFormData } from "@/types/iridology";
import { Activity, Brain, Heart, Leaf, Sun, Zap, AlertCircle, Sparkles } from "lucide-react";

interface IridologyAnalysisFormProps {
  data: IridologyFormData;
  onChange: (data: Partial<IridologyFormData>) => void;
  readOnly?: boolean;
}

export const IridologyAnalysisForm: React.FC<IridologyAnalysisFormProps> = ({
  data,
  onChange,
  readOnly = false,
}) => {
  const handleCheckboxChange = (field: keyof IridologyFormData, value: boolean) => {
    onChange({ [field]: value });
  };

  return (
    <div className="space-y-6">
      {/* Terrain dominant */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="w-5 h-5 text-primary" />
            Terrain dominant
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              { key: 'terrain_nervous', label: 'Nerveux' },
              { key: 'terrain_digestive', label: 'Digestif' },
              { key: 'terrain_circulatory', label: 'Circulatoire' },
              { key: 'terrain_eliminatory', label: 'Éliminatoire' },
              { key: 'terrain_mixed', label: 'Mixte' },
            ].map(({ key, label }) => (
              <div key={key} className="flex items-center space-x-2">
                <Checkbox
                  id={key}
                  checked={data[key as keyof IridologyFormData] as boolean}
                  onCheckedChange={(checked) => handleCheckboxChange(key as keyof IridologyFormData, !!checked)}
                  disabled={readOnly}
                />
                <Label htmlFor={key} className="text-sm cursor-pointer">
                  {label}
                </Label>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Vitalité globale */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Zap className="w-5 h-5 text-yellow-500" />
            Vitalité globale
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <RadioGroup
            value={data.vitality_level || ''}
            onValueChange={(value) => onChange({ vitality_level: value as any })}
            disabled={readOnly}
            className="flex flex-wrap gap-4"
          >
            {[
              { value: 'good', label: 'Bonne', color: 'text-green-600' },
              { value: 'average', label: 'Moyenne', color: 'text-yellow-600' },
              { value: 'needs_support', label: 'À soutenir', color: 'text-orange-600' },
            ].map(({ value, label, color }) => (
              <div key={value} className="flex items-center space-x-2">
                <RadioGroupItem value={value} id={`vitality-${value}`} />
                <Label htmlFor={`vitality-${value}`} className={`text-sm cursor-pointer ${color}`}>
                  {label}
                </Label>
              </div>
            ))}
          </RadioGroup>

          <div className="space-y-2">
            <Label htmlFor="vitality_comments" className="text-sm">Commentaires</Label>
            <Textarea
              id="vitality_comments"
              value={data.vitality_comments}
              onChange={(e) => onChange({ vitality_comments: e.target.value })}
              placeholder="Observations sur la vitalité..."
              disabled={readOnly}
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      {/* Capacités d'adaptation */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Brain className="w-5 h-5 text-purple-500" />
            Capacités d'adaptation
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {[
              { key: 'adaptation_good', label: 'Bonne' },
              { key: 'adaptation_stress_sensitivity', label: 'Sensibilité au stress' },
              { key: 'adaptation_fatigue', label: 'Fatigabilité possible' },
            ].map(({ key, label }) => (
              <div key={key} className="flex items-center space-x-2">
                <Checkbox
                  id={key}
                  checked={data[key as keyof IridologyFormData] as boolean}
                  onCheckedChange={(checked) => handleCheckboxChange(key as keyof IridologyFormData, !!checked)}
                  disabled={readOnly}
                />
                <Label htmlFor={key} className="text-sm cursor-pointer">
                  {label}
                </Label>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Zones de sensibilité et à soutenir */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-orange-500" />
            Zones de sensibilité
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="sensitivity_zones" className="text-sm">Zones de sensibilité observées</Label>
            <Textarea
              id="sensitivity_zones"
              value={data.sensitivity_zones}
              onChange={(e) => onChange({ sensitivity_zones: e.target.value })}
              placeholder="Décrire les zones de sensibilité observées..."
              disabled={readOnly}
              rows={2}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="support_zones" className="text-sm">Zones à soutenir prioritairement</Label>
            <Textarea
              id="support_zones"
              value={data.support_zones}
              onChange={(e) => onChange({ support_zones: e.target.value })}
              placeholder="Décrire les zones à soutenir en priorité..."
              disabled={readOnly}
              rows={2}
            />
          </div>
        </CardContent>
      </Card>

      {/* Signes de surcharge */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Heart className="w-5 h-5 text-red-500" />
            Signes de surcharge
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              { key: 'overload_digestive', label: 'Digestive' },
              { key: 'overload_nervous', label: 'Nerveuse' },
              { key: 'overload_emotional', label: 'Émotionnelle' },
              { key: 'overload_eliminatory', label: 'Éliminatoire' },
              { key: 'overload_circulatory', label: 'Circulatoire' },
            ].map(({ key, label }) => (
              <div key={key} className="flex items-center space-x-2">
                <Checkbox
                  id={key}
                  checked={data[key as keyof IridologyFormData] as boolean}
                  onCheckedChange={(checked) => handleCheckboxChange(key as keyof IridologyFormData, !!checked)}
                  disabled={readOnly}
                />
                <Label htmlFor={key} className="text-sm cursor-pointer">
                  {label}
                </Label>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Axes d'accompagnement */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-primary" />
            Axes d'accompagnement proposés
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            {[
              { key: 'axis_hygiene', label: 'Hygiène de vie', icon: Sun },
              { key: 'axis_nutrition', label: 'Ajustements alimentaires', icon: Leaf },
              { key: 'axis_plants', label: 'Soutien par les plantes', icon: Leaf },
              { key: 'axis_stress', label: 'Gestion du stress', icon: Brain },
              { key: 'axis_seasonal', label: 'Accompagnement saisonnier', icon: Sun },
            ].map(({ key, label, icon: Icon }) => (
              <div key={key} className="flex items-center space-x-2">
                <Checkbox
                  id={key}
                  checked={data[key as keyof IridologyFormData] as boolean}
                  onCheckedChange={(checked) => handleCheckboxChange(key as keyof IridologyFormData, !!checked)}
                  disabled={readOnly}
                />
                <Label htmlFor={key} className="text-sm cursor-pointer flex items-center gap-1">
                  <Icon className="w-3 h-3 text-muted-foreground" />
                  {label}
                </Label>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Observations complémentaires */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">Observations complémentaires</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            id="additional_observations"
            value={data.additional_observations}
            onChange={(e) => onChange({ additional_observations: e.target.value })}
            placeholder="Ajouter des observations complémentaires..."
            disabled={readOnly}
            rows={4}
          />
        </CardContent>
      </Card>

      {/* Legal disclaimer */}
      <div className="bg-muted/50 rounded-lg p-4 text-sm text-muted-foreground">
        <p className="italic">
          <strong>Orientation de l'accompagnement :</strong> Cette analyse iridologique oriente vers un 
          accompagnement naturopathique personnalisé visant à soutenir l'équilibre global et la vitalité du consultant.
        </p>
        <p className="mt-2 text-xs">
          ⚠️ L'iridologie est un outil d'observation du terrain et ne constitue pas un diagnostic médical.
        </p>
      </div>
    </div>
  );
};
